export interface SpreadsheetPreviewLimits {
  rowLimit: number;
  columnLimit: number;
  cellLimit: number;
  sheetLimit: number;
}

export const SPREADSHEET_MAX_PREVIEW_BYTES = 50 * 1024 * 1024;
export const SPREADSHEET_PARSE_TIMEOUT_MS = 15_000;
export const SPREADSHEET_CANVAS_LIMITS: SpreadsheetPreviewLimits = { rowLimit: 24, columnLimit: 10, cellLimit: 240, sheetLimit: 100 };
export const SPREADSHEET_DETAIL_LIMITS: SpreadsheetPreviewLimits = { rowLimit: 2_000, columnLimit: 256, cellLimit: 200_000, sheetLimit: 100 };

export interface SpreadsheetAxisEntry {
  index: number;
  label: string;
  size: number;
}

export interface SpreadsheetCellStyle {
  background?: string;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  align?: "left" | "center" | "right";
  verticalAlign?: "top" | "middle" | "bottom";
  wrap?: boolean;
  borders?: Partial<Record<"top" | "right" | "bottom" | "left", string>>;
}

export interface SpreadsheetCell {
  row: number;
  column: number;
  address: string;
  display: string;
  raw: string;
  formula?: string;
  style?: SpreadsheetCellStyle;
  rowSpan?: number;
  columnSpan?: number;
}

export interface SpreadsheetRange {
  startRow: number;
  startColumn: number;
  endRow: number;
  endColumn: number;
}

export interface SpreadsheetSheetModel {
  name: string;
  rows: SpreadsheetAxisEntry[];
  columns: SpreadsheetAxisEntry[];
  cells: Record<string, SpreadsheetCell>;
  merges: SpreadsheetRange[];
  truncatedRows: boolean;
  truncatedColumns: boolean;
  truncatedCells: boolean;
}

export interface SpreadsheetWorkbookSummary {
  sheetNames: string[];
  totalVisibleSheets: number;
  truncatedSheets: boolean;
}

export type SpreadsheetWorkerRequest =
  | { kind: "load"; requestId: number; bytes: ArrayBuffer; format: "xlsx" | "xls"; limits: SpreadsheetPreviewLimits }
  | { kind: "sheet"; requestId: number; sheetName: string; limits: SpreadsheetPreviewLimits }
  | { kind: "cancel"; requestId: number }
  | { kind: "dispose" };

export type SpreadsheetWorkerResponse =
  | { kind: "loaded"; requestId: number; summary: SpreadsheetWorkbookSummary; sheet: SpreadsheetSheetModel }
  | { kind: "sheet"; requestId: number; sheet: SpreadsheetSheetModel }
  | { kind: "error"; requestId: number; message: string };

export function spreadsheetCellKey(row: number, column: number): string {
  return `${row}:${column}`;
}

export function normalizeSpreadsheetRange(first: { row: number; column: number }, second: { row: number; column: number }): SpreadsheetRange {
  return {
    startRow: Math.min(first.row, second.row),
    startColumn: Math.min(first.column, second.column),
    endRow: Math.max(first.row, second.row),
    endColumn: Math.max(first.column, second.column)
  };
}

export function spreadsheetRangeToTsv(sheet: SpreadsheetSheetModel, range: SpreadsheetRange): string {
  const covered = new Set<string>();
  for (const merge of sheet.merges) {
    for (let row = merge.startRow; row <= merge.endRow; row += 1) {
      for (let column = merge.startColumn; column <= merge.endColumn; column += 1) {
        if (row !== merge.startRow || column !== merge.startColumn) covered.add(spreadsheetCellKey(row, column));
      }
    }
  }
  const rows: string[] = [];
  for (let row = range.startRow; row <= range.endRow; row += 1) {
    const cells: string[] = [];
    for (let column = range.startColumn; column <= range.endColumn; column += 1) {
      const key = spreadsheetCellKey(row, column);
      cells.push(covered.has(key) ? "" : (sheet.cells[key]?.display ?? ""));
    }
    rows.push(cells.join("\t"));
  }
  return rows.join("\n");
}

export function spreadsheetMergeOwner(sheet: SpreadsheetSheetModel, point: { row: number; column: number }): { row: number; column: number } {
  const merge = sheet.merges.find((candidate) => point.row >= candidate.startRow && point.row <= candidate.endRow && point.column >= candidate.startColumn && point.column <= candidate.endColumn);
  return merge ? { row: merge.startRow, column: merge.startColumn } : point;
}

export function clampSpreadsheetZoom(value: number): number {
  return Math.min(1.5, Math.max(.75, value));
}

export function spreadsheetVisibleAxis(offsets: number[], sizes: number[], scroll: number, viewportSize: number, overscan = 2): number[] {
  if (sizes.length === 0) return [];
  const boundaryStart = Math.max(0, scroll);
  const found = offsets.findIndex((offset, index) => offset + (sizes[index] ?? 0) > boundaryStart);
  const start = found < 0 ? sizes.length - 1 : found;
  let end = start;
  const boundaryEnd = boundaryStart + viewportSize;
  while (end < sizes.length - 1 && (offsets[end] ?? 0) < boundaryEnd) end += 1;
  const rangeStart = Math.max(0, start - overscan);
  const rangeEnd = Math.min(sizes.length - 1, end + overscan);
  return Array.from({ length: rangeEnd - rangeStart + 1 }, (_, index) => rangeStart + index);
}
